import { apiClient } from "../../../../../lib/axios";
import type {
  Report,
  CreateReportBody,
  UpdateReportBody,
  NarrationResult,
  PdfResult,
} from "./types";

// The shared apiClient has a 10s timeout. Report narration (sequential
// ElevenLabs calls, one per page), PDF export (headless Chromium render), and
// AI narrative generation all routinely exceed that, so these calls override
// the timeout per-request. 0 disables the axios timeout entirely.
const LONG_RUNNING_TIMEOUT = 180_000;

export const reportingApi = {
  list: async (clientId?: string | null): Promise<Report[]> => {
    const params = clientId ? { client_id: clientId } : undefined;
    const res = await apiClient.get("/admin/reports", { params });
    return res.data.data;
  },

  get: async (id: string): Promise<Report> => {
    const res = await apiClient.get(`/admin/reports/${id}`);
    return res.data.data;
  },

  create: async (body: CreateReportBody): Promise<Report> => {
    const res = await apiClient.post("/admin/reports", body);
    return res.data.data;
  },

  update: async (id: string, body: UpdateReportBody): Promise<Report> => {
    const res = await apiClient.put(`/admin/reports/${id}`, body);
    return res.data.data;
  },

  remove: async (id: string): Promise<{ id: string }> => {
    const res = await apiClient.delete(`/admin/reports/${id}`);
    return res.data.data;
  },

  generateNarrative: async (id: string, hint?: string): Promise<{ text: string }> => {
    const res = await apiClient.post(
      `/admin/reports/${id}/generate-narrative`,
      { hint },
      { timeout: LONG_RUNNING_TIMEOUT },
    );
    return res.data.data;
  },

  generateActionPlan: async (
    id: string,
    hint?: string,
  ): Promise<{ items: string[] }> => {
    const res = await apiClient.post(
      `/admin/reports/${id}/generate-action-plan`,
      { hint },
      { timeout: LONG_RUNNING_TIMEOUT },
    );
    return res.data.data;
  },

  narrate: async (id: string): Promise<NarrationResult> => {
    const res = await apiClient.post(
      `/admin/reports/${id}/narrate`,
      undefined,
      { timeout: LONG_RUNNING_TIMEOUT },
    );
    return res.data.data;
  },

  getAudio: async (id: string): Promise<NarrationResult> => {
    const res = await apiClient.get(`/admin/reports/${id}/audio`);
    return res.data.data;
  },

  exportPdf: async (id: string): Promise<PdfResult> => {
    const res = await apiClient.post(
      `/admin/reports/${id}/export-pdf`,
      undefined,
      { timeout: LONG_RUNNING_TIMEOUT },
    );
    return res.data.data;
  },

  getPdf: async (id: string): Promise<PdfResult> => {
    const res = await apiClient.get(`/admin/reports/${id}/pdf`);
    return res.data.data;
  },

  createLogoUploadUrl: async (payload: {
    filename: string;
    content_type: string;
  }): Promise<{ upload_url: string; file_url: string; key: string }> => {
    const res = await apiClient.post("/admin/reports/logo-upload-url", payload);
    return res.data;
  },

  createImageUploadUrl: async (payload: {
    filename: string;
    content_type: string;
  }): Promise<{ upload_url: string; file_url: string; key: string }> => {
    const res = await apiClient.post("/admin/reports/image-upload-url", payload);
    return res.data;
  },

  exportPptx: async (id: string): Promise<{ pptx_url: string }> => {
    const res = await apiClient.post(
      `/admin/reports/${id}/export-pptx`,
      undefined,
      { timeout: LONG_RUNNING_TIMEOUT },
    );
    return res.data.data;
  },
};
